import React, { useEffect } from 'react';
import { useAtomValue } from 'jotai';
import { useDispatch, useSelector } from 'react-redux';
import { CSS } from '@dnd-kit/utilities';
import { useSortable } from '@dnd-kit/sortable';
import { metricSelector, selectedMetricSelector } from '@store/metricsChartContainer/selectors';
import { globalOrderBySelector } from '@store/trends/base/selectors';
import { horizontalLayoutAtom, errorDataAtom } from '@src/components/metrics-panel-hz/atoms';
import DragHandle from '@components/common/DnD/DragHandle';
import { filterNAMetricsAtom } from '@src/atoms/page';
import { selectedMetricChart, setMetrics } from '@store/metricsChartContainer/actions';
import { preserveOrder } from '@components/common/DnD/utils';
import { setDimensionMetrics } from '@store/trends/base/actions';
import { getMetricMetaByKey } from '@src/utils/metrics';
import { StyledTag, StyledDiv } from './styled';
import { scrollChartToView } from '@src/components/metrics-panel-hz/hooks/useAutoScroll';
import { addSecondaryOnlyMetrics, getSecondaryOnlyCleanedUpMetrics } from './MetricTags';
import { tid } from '@utils/test';
import { DeletedMetric } from '@components/common/DeletedMetric';
import { activeDashboardSelector } from '@src/store/tempDashboard/selectors';
import { getPresetDefaultMetric } from '@src/utils/presets/utils';
import { useDrilldownMeta } from '@src/hooks/common/useDrilldownMeta';

const MetricTag = ({ metric, metrics }) => {
  const errorData = useAtomValue(errorDataAtom);
  const primaryMetric = useSelector(selectedMetricSelector);
  const globalOrderByMetric = useSelector(globalOrderBySelector);
  const dispatch = useDispatch();
  const { flowId, frustrationSignalId } = useDrilldownMeta();
  const { short, secondaryOnly = false } = getMetricMetaByKey({
    metricKey: metric,
    flowOverride: !!flowId,
    frustrationSignalOverride: !!frustrationSignalId,
  });
  const metricsList = useSelector(metricSelector);
  const horizontalLayout = useAtomValue(horizontalLayoutAtom);
  const metricsToNA = useAtomValue(filterNAMetricsAtom);
  const canEdit = ![primaryMetric].includes(metric);
  const isNaMode: boolean = metricsToNA.includes(metric);
  const isGlobalOrderBy = metric === globalOrderByMetric;
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: metric,
  });
  const activeDashboard = useSelector(activeDashboardSelector) as { usecaseKey?: string };
  const { usecaseKey } = activeDashboard || {};

  const dndStyle = {
    transform: CSS.Transform.toString(transform),
    transition: transition || undefined,
  };

  useEffect(() => {
    if (errorData[primaryMetric]) {
      const defaultMetric = getPresetDefaultMetric(usecaseKey);
      dispatch(selectedMetricChart(defaultMetric, undefined, true));
      dispatch(setMetrics(preserveOrder(metricsList, [...metricsList, defaultMetric])));
    }
  }, [errorData, primaryMetric, dispatch, metricsList, usecaseKey]);

  const onCloseFn = (metric) => () => {
    const filteredMetrics = metrics.filter((m) => m !== metric);
    const filteredSecondaryOnlyMetrics = getSecondaryOnlyCleanedUpMetrics(filteredMetrics);
    dispatch(setDimensionMetrics(addSecondaryOnlyMetrics(filteredSecondaryOnlyMetrics)));
  };

  const handleClickFn = (metric) => () => {
    if (!short) {
      return;
    }
    // TODO: add set metrics to primary effect workflow?
    dispatch(selectedMetricChart(metric, undefined, true));
    dispatch(setMetrics(preserveOrder(metricsList, [...metricsList, metric])));
    if (!horizontalLayout) {
      scrollChartToView(metric);
    }
  };
  if (secondaryOnly) {
    return null;
  } else {
    return (
      <StyledDiv>
        <div id={metric} ref={setNodeRef} style={dndStyle} {...tid(`metric-tag::${metric}`)}>
          <DragHandle {...attributes} {...listeners} />
          <StyledTag
            key={metric}
            color={canEdit ? 'default' : 'blue'}
            closable={canEdit && !isGlobalOrderBy}
            onClose={onCloseFn(metric)}
            icon={undefined}
            $disableMode={isNaMode}
            contentEditable={false}
          >
            <span
              onClick={handleClickFn(metric)}
              style={isNaMode ? { opacity: 0.5, pointerEvents: 'none' } : {}}
            >
              {errorData[metric] ? <DeletedMetric /> : short}
            </span>
          </StyledTag>
        </div>
      </StyledDiv>
    );
  }
};

export default MetricTag;
